import locale from '@glu/locale';
import classNames from 'classnames';
import PageSelectorStartToEnd from '../PageSelectorStartToEnd/PageSelectorStartToEnd';
import PageDisplayCount from '../PageDisplayCount/PageDisplayCount';
import PageSelector from '../PageSelector/PageSelector';
import useStyles from './Pagination.styles';

export function getPageRange({
  page,
  pageSize,
  totalItems,
}: {
  page: IPaginationProps['page'];
  pageSize: IPaginationProps['pageSize'];
  totalItems: IPaginationProps['totalItems'];
}) {
  const start = totalItems === 0 ? 0 : pageSize * page + 1;
  const end = start + pageSize - 1;
  const endResult = end > totalItems ? totalItems : end;
  if (start === endResult) {
    return locale.get('dataComponents.viewOf', `${start}`, `${totalItems}`);
  }
  return locale.get('dataComponents.viewOf', `${start}-${endResult}`, `${totalItems}`);
}

export interface IPaginationProps {
  /** Hides the dropdown menu to change the page size. */
  hidePageSizeOptions?: boolean;
  /**
   * Updates the current page number to the next highest page number.
   * Triggered when the right arrow is clicked.
   */
  onNextPage(): void;
  /**
   * Updates the current page number to the next lowest page number.
   * Triggered when the left arrow is clicked.
   */
  onPreviousPage(): void;
  /** handles the page change. Takes page (number) as a parameter. The index starts at 0 */
  onPageChange(page: number): void;
  /** handles page size change. Takes the size (number) as a parameter */
  onPageSizeChange(pageSize: number): void;
  /** The current displayed page number */
  page: number;
  /** The number of rows to be displayed per grid page */
  pageSize: number;
  /** Allows custom page sizes to be used by the page size dropdown */
  pageSizeOptions?: number[];
  /** The total number of rows in the data */
  totalItems: number;
  /** CSS classes applied to the root element of this component. */
  className?: string;
  /** Html id attribute applied to the root element of this component. */
  htmlId?: string;
}

const Pagination = ({
  className,
  hidePageSizeOptions,
  htmlId,
  onNextPage,
  onPageChange: onGoToPage,
  onPageSizeChange,
  onPreviousPage,
  page,
  pageSize: passedPageSize,
  pageSizeOptions,
  totalItems,
}: IPaginationProps) => {
  const classes = useStyles();
  const pageSize = !passedPageSize ? 1 : Math.max(1, passedPageSize);
  const numberOfPages = Math.ceil(totalItems / pageSize);
  const pages = [...Array(numberOfPages)].map((_, index) => index);
  const last = pages[pages.length - 1] || 0;

  return (
    <div className={classNames(classes.wrapper, className)} id={htmlId}>
      <div className={classes.range} data-qa="pagination-range">
        {getPageRange({ page, pageSize, totalItems })}
      </div>
      <div>
        {!hidePageSizeOptions && (
          <PageDisplayCount
            onChange={onPageSizeChange}
            pageSize={pageSize}
            pageSizeOptions={pageSizeOptions}
          />
        )}
        <PageSelector
          last={last}
          onNextPage={onNextPage}
          onPreviousPage={onPreviousPage}
          page={page}
        >
          <PageSelectorStartToEnd
            last={last}
            numberOfPages={numberOfPages}
            onGoToPage={onGoToPage}
            page={page}
            pages={pages}
          />
        </PageSelector>
      </div>
    </div>
  );
};

export default Pagination;
